import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Ticket, Gift, Sparkles, CalendarDays, Trophy } from "lucide-react";

const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

function monthLabel(m) {
  if (!m) return "";
  const [, mm] = m.split("-");
  return MONTHS[parseInt(mm, 10) - 1];
}

export default function RaffleCustomerCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/customer/raffle").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;

  const { raffle, tickets_count, tickets, month } = data;
  const hasPrizes = raffle?.published && (raffle.prizes || []).length > 0;
  const mName = monthLabel(month);

  return (
    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#7C3AED] via-[#6D28D9] to-[#4C1D95] text-white shadow-[0_30px_60px_-20px_rgba(124,58,237,0.55)]" data-testid="customer-raffle-card">
      {/* Decorative glow */}
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-amber-300/30 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-16 -left-16 w-72 h-72 rounded-full bg-white/10 blur-3xl pointer-events-none" />

      <div className="relative p-6 md:p-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-amber-300 grid place-items-center">
              <Trophy className="w-6 h-6 text-[#5B21B6]" />
            </div>
            <div>
              <div className="overline text-amber-300 text-[11px]">RIFA OFERTAS MÉXICO</div>
              <div className="font-display text-2xl md:text-3xl tracking-tight leading-tight">
                Rifa del mes de {mName}
              </div>
            </div>
          </div>
          <div className="bg-amber-300 text-[#3B1565] rounded-2xl px-4 py-2 text-center">
            <div className="text-[10px] uppercase tracking-wider opacity-80">Tus boletos</div>
            <div className="font-display text-3xl">{tickets_count}</div>
          </div>
        </div>

        {hasPrizes ? (
          <>
            <div className="text-sm text-white/90 mt-5">
              <Sparkles className="inline w-4 h-4 text-amber-300 mr-1" />
              Este mes rifamos:
            </div>
            <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="raffle-prizes-customer">
              {raffle.prizes.map((p) => (
                <div key={p.position} className="bg-white/15 backdrop-blur border border-white/30 rounded-2xl p-4 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-amber-300 grid place-items-center shrink-0">
                    <span className="text-[#5B21B6] font-display font-bold">{p.position}</span>
                  </div>
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-wider text-amber-200">Premio {p.position}</div>
                    <div className="font-medium leading-snug mt-0.5">{p.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="mt-5 text-sm text-white/85">
            Los premios de este mes aún no se publican. ¡Sigue acumulando compras para tener más boletos!
          </div>
        )}

        <div className="mt-5 flex items-center gap-2 text-xs text-white/80">
          <CalendarDays className="w-3.5 h-3.5" />
          <span>
            Sorteo: <strong>{raffle?.draw_date ? new Date(raffle.draw_date + "T12:00:00Z").toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }) : "último día del mes"}</strong>
          </span>
        </div>

        {/* Tickets list */}
        {tickets_count > 0 && (
          <div className="mt-5 bg-white/10 backdrop-blur rounded-2xl border border-white/20 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Ticket className="w-4 h-4 text-amber-300" />
              <div className="font-medium text-sm">Mis boletos del mes de {mName}</div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="customer-tickets-list">
              {(tickets || []).map((t) => (
                <div key={t.id} className="bg-amber-200 text-[#3B1565] rounded-lg p-2 text-center" data-testid={`ticket-${t.id}`}>
                  <Ticket className="w-3.5 h-3.5 mx-auto opacity-70" />
                  <div className="font-mono text-[10px] mt-1 truncate">{t.id.slice(0, 8)}</div>
                  <div className="text-[10px] opacity-80 truncate">{t.business_name}</div>
                </div>
              ))}
            </div>
            {tickets_count > (tickets || []).length && (
              <div className="text-[11px] text-white/70 mt-2">+ {tickets_count - tickets.length} boletos más</div>
            )}
          </div>
        )}

        {tickets_count === 0 && (
          <div className="mt-5 bg-white/10 backdrop-blur rounded-2xl border border-white/20 p-4 text-center text-sm text-white/85">
            <Gift className="w-5 h-5 mx-auto text-amber-300 mb-1" />
            Aún no tienes boletos este mes. <strong>Cada compra en un comercio OFERTAS te da 1 boleto digital.</strong>
          </div>
        )}
      </div>
    </div>
  );
}
